import React from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove'; // For no change

// Helper function to render appropriate icon based on percentage
const renderChangeIcon = (percentage: number | undefined) => {
	if (percentage === undefined) return null;
	if (percentage > 0) {
		return <ArrowUpwardIcon fontSize="small" style={{ color: 'green' }} />;
	} else if (percentage < 0) {
		return <ArrowDownwardIcon fontSize="small" style={{ color: 'red' }} />;
	} else {
		return <RemoveIcon fontSize="small" style={{ color: 'gray' }} />; // No change
	}
};

const BasicStatsData: React.FC<{
	basicStatsData: BasicStatsData | null;
}> = ({ basicStatsData }) => {
	// Safely check if basicStatsData and its properties exist
	const activeUsers = basicStatsData?.currentData?.activeUsers;
	const eventCount = basicStatsData?.currentData?.eventCount;
	const newUsers = basicStatsData?.currentData?.newUsers;

	const activeUsersChange = basicStatsData?.percentageChange?.activeUsers;
	const eventCountChange = basicStatsData?.percentageChange?.eventCount;
	const newUsersChange = basicStatsData?.percentageChange?.newUsers;

	return (
		<div>
			<Grid container spacing={2} padding={4} pt={4} pb={4}>
				{/* Active Users */}
				<Grid item xs={12} sm={4}>
					<Paper
						elevation={3} 
						style={{ padding: '16px', textAlign: 'center' }}
					>
						<Typography variant="h6" color="textSecondary">
							Active users
						</Typography>
						<Typography variant="h4" component="p">
							{activeUsers}
						</Typography>
						{activeUsersChange !== undefined && (
							<Typography
								variant="body2"
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									color:
										activeUsersChange > 0 ? 'green' : 'red',
								}}
							>
								{renderChangeIcon(activeUsersChange)}{' '}
								{Math.abs(activeUsersChange)}%
							</Typography>
						)}
						{/* Definition */}
						<Typography
							variant="body2"
							color="textSecondary"
							style={{ marginTop: '8px' }}
						>
							The number of unique users who engaged with your app
							during the selected time period.
						</Typography>
					</Paper>
				</Grid>

				{/* Event Count */}
				<Grid item xs={12} sm={4}>
					<Paper
						elevation={3}
						style={{ padding: '16px', textAlign: 'center' }}
					>
						<Typography variant="h6" color="textSecondary">
							Event count
						</Typography>
						<Typography variant="h4" component="p">
							{eventCount}
						</Typography>
						{eventCountChange !== undefined && (
							<Typography
								variant="body2"
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									color:
										eventCountChange > 0 ? 'green' : 'red',
								}}
							>
								{renderChangeIcon(eventCountChange)}{' '}
								{Math.abs(eventCountChange)}%
							</Typography>
						)}
						{/* Definition */}
						<Typography
							variant="body2"
							color="textSecondary"
							style={{ marginTop: '8px' }}
						>
							The total number of events (e.g., clicks, page
							views, form submissions) recorded during the
							selected time period.
						</Typography>
					</Paper>
				</Grid>

				{/* New Users */}
				<Grid item xs={12} sm={4}>
					<Paper
						elevation={3}
						style={{ padding: '16px', textAlign: 'center' }}
					>
						<Typography variant="h6" color="textSecondary">
							New users
						</Typography>
						<Typography variant="h4" component="p">
							{newUsers}
						</Typography>
						{newUsersChange !== undefined && (
							<Typography
								variant="body2"
								style={{
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									color: newUsersChange > 0 ? 'green' : 'red',
								}}
							>
								{renderChangeIcon(newUsersChange)}{' '}
								{Math.abs(newUsersChange)}%
							</Typography>
						)}
						{/* Definition */}
						<Typography
							variant="body2"
							color="textSecondary"
							style={{ marginTop: '8px' }}
						>
							Users who interacted with your app for the first
							time during the selected time period.
						</Typography>
					</Paper>
				</Grid>
			</Grid>
		</div>
	);
};

export default BasicStatsData;
